<template>
  <VMultiModal :showing="showing">
    <EditorDeckModalsSecureAccessUsers
      v-if="showing && show === SecureAccessModals.Users"
      :key="SecureAccessModals.Users"
      :showing="showing"
      :deck="deck"
      @update:deck-internal-access="
        $emit('update:deck-internal-access', $event), (notification = { title: `Internal Access ${$event ? 'Granted' : 'Removed'}` })
      "
      @close="$emit('update:showing', false)"
      @user-created="
        notification = { title: 'New User Added', text: `${$event} has been added. Click “Give View Only Access” to grant access.` }
      "
      @error="notification = { title: 'An Error Occurred', text: 'Not all changes were saved successfully', isError: true }"
      @goto:secure-links="show = SecureAccessModals.Links"
      @goto:remove-self="show = SecureAccessModals.ConfirmRemoveSelf"
    />

    <EditorDeckModalsSecureLink
      v-if="showing && show === SecureAccessModals.Links"
      :key="SecureAccessModals.Links"
      :showing="showing"
      :deck="deck"
      @close="$emit('update:showing', false)"
      @goto:secure-users="show = SecureAccessModals.Users"
      @goto:create-link="show = SecureAccessModals.CreateLink"
      @link-destroyed="notification = { title: 'Secure Link Destroyed' }"
      @error="notification = { title: 'An Error Occurred', text: 'Not all changes were saved successfully', isError: true }"
    />

    <EditorDeckModalsCreateSecureLink
      v-if="showing && show === SecureAccessModals.CreateLink"
      :key="SecureAccessModals.CreateLink"
      :showing="showing"
      :deck="deck"
      @goto:secure-links="show = SecureAccessModals.Links"
      @link-created="notification = { title: 'New Secure Link Created' }"
    />

    <VModal
      v-if="showing && show === SecureAccessModals.ConfirmRemoveSelf"
      :key="SecureAccessModals.ConfirmRemoveSelf"
      :showing="showing"
      container-classes="_max-w-lg"
      data-testid="remove-access-confirmation-modal"
      @update:showing="show = SecureAccessModals.Users"
    >
      <h2 class="text-lg text-gray-900 mb-4">Revoke Access for {{ userState.user?.name }}?</h2>
      <div class="text-gray-700 text-base">
        Are you sure you want to remove your access to this Deck? If you remove your access, you will no longer be able to view or manage
        access to this Deck.
      </div>
      <div class="flex justify-end">
        <div class="flex">
          <VButton size="lg" data-testid="cancel-btn" @click="show = SecureAccessModals.Users">Cancel</VButton>
          <VButton class="ml-3" size="lg" variant="copper" data-testid="revoke-self-access-btn" @click="revokeSelfAccess">Confirm</VButton>
        </div>
      </div>
    </VModal>
    <VNotification
      key="SecureAccessModals.ConfirmRemoveSelf"
      :show="!!notification"
      :delay="4000"
      class="absolute top-4 right-4 w-96"
      @dismissed="notification = null"
    >
      <template #icon>
        <CheckCircleIcon v-if="notification && !notification.isError" class="w-6 text-copper-400" />
        <XCircleIcon v-else class="w-6 text-red-800" />
      </template>

      <template #title>{{ notification && notification.title }}</template>
      <template v-if="notification && notification.text" #default>{{ notification.text }}</template>
    </VNotification>
  </VMultiModal>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'

import type { EditDeck } from '~/composables/useEditDeck'

enum SecureAccessModals {
  Users,
  Links,
  CreateLink,
  ConfirmRemoveSelf,
}

type NotificationSpec = { title: string; text?: string; isError?: boolean }

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['link-destroyed', 'link-created', 'update:deck', 'update:showing', 'update:deck-internal-access'])

watch(
  toRef(props, 'deck'),
  newValue => {
    show.value = newValue.edit_access_type === 'private' ? SecureAccessModals.Links : SecureAccessModals.Users
  },
  {
    deep: true,
  }
)

const { state: userState } = useUser()
const show = ref<SecureAccessModals>(props.deck.edit_access_type === 'private' ? SecureAccessModals.Links : SecureAccessModals.Users)
const notification: Ref<NotificationSpec | null> = ref(null)

const { revokeEditAccess } = useEditSecureAccess({
  deck: computed(() => (props.showing ? props.deck : null)),
  onError(_error: string) {
    notification.value = {
      title: 'An error occurred',
      text: 'Not all changes were saved successfully',
      isError: true,
    }
  },
  loadData: false,
})

// Revoke access for the current logged in user, and redirect them to the correct
// location so that they cannot see the deck any longer.
async function revokeSelfAccess(): Promise<void> {
  if (!userState.value.user) return
  revokeEditAccess(userState.value.user as any)
  emit('update:showing', false)

  // @todo 2022-11-09 jamesnakagawa confirm this is the right path
  window.location.href = '/'
}
</script>
